import { useCallback, useEffect, useMemo } from "react";
import {
  HEALTH_CHECK_TRIGGER_EVENT,
  HEALTH_DOWN_POLL_BASE_MS,
  RETRY_COOLDOWN_BASE_SECONDS,
  RETRY_COOLDOWN_MAX_SECONDS,
} from "components/common/ErrorHandler/ErrorHandler.constants";
import { useExponentialCooldown } from "hooks/useExponentialCooldown";
import { useHealthCheck } from "hooks/useHealthCheck";
import type { HealthCheckResult } from "hooks/useHealthCheck/useHealthCheck.types";

const AUTH_MAINTENANCE_COOLDOWN_STORAGE_KEY = "authMaintenanceCheckAgainCooldownState";

export function isMaintenanceHealthResult(healthData: HealthCheckResult | undefined): boolean {
  if (!healthData) {
    return false;
  }
  const statusCode = healthData.statusCode;
  const status = healthData.status;
  return statusCode === 503 || status === "maintenance";
}

export const useMaintenanceStatus = () => {
  const { data, isFetching, isError, refetch, dataUpdatedAt } = useHealthCheck({
    enabled: true,
  });

  const effectiveData: HealthCheckResult | undefined = useMemo(() => {
    if (data) {
      return data;
    }
    if (isError) {
      return { status: "unknown", ok: false };
    }
    return undefined;
  }, [data, isError]);

  const isMaintenance = isMaintenanceHealthResult(effectiveData);
  const isChecking = isFetching && effectiveData === undefined;

  useEffect(() => {
    const handleHealthCheckTrigger = () => {
      void refetch();
    };
    window.addEventListener(HEALTH_CHECK_TRIGGER_EVENT, handleHealthCheckTrigger);
    return () => {
      window.removeEventListener(HEALTH_CHECK_TRIGGER_EVENT, handleHealthCheckTrigger);
    };
  }, [refetch]);

  useEffect(() => {
    if (!isMaintenance) {
      return;
    }
    const timerId = window.setInterval(() => {
      void refetch();
    }, HEALTH_DOWN_POLL_BASE_MS);
    return () => {
      window.clearInterval(timerId);
    };
  }, [isMaintenance, refetch]);

  const { cooldownRemaining, isOnCooldown, triggerCooldown } = useExponentialCooldown({
    baseSeconds: RETRY_COOLDOWN_BASE_SECONDS,
    maxCooldownSeconds: RETRY_COOLDOWN_MAX_SECONDS,
    storageKey: AUTH_MAINTENANCE_COOLDOWN_STORAGE_KEY,
  });

  const checkAgain = useCallback(() => {
    if (isOnCooldown) {
      return;
    }
    triggerCooldown();
    void refetch();
  }, [isOnCooldown, triggerCooldown, refetch]);

  return {
    isMaintenance,
    isChecking,
    checkAgain,
    isOnCooldown,
    cooldownRemaining,
    maintenanceDetails: effectiveData?.maintenance,
    lastCheckedAt: dataUpdatedAt ? new Date(dataUpdatedAt) : undefined,
  };
};
